import { useRef, useState } from "react";

const TRIGGER = 56;

export default function PullToRefresh({ onRefresh, children }) {
    const rootRef = useRef(null);
    const startY = useRef(null);
    const distanceRef = useRef(0);
    const dragging = useRef(false);
    const refreshingRef = useRef(false);

    const [distance, setDistance] = useState(0);
    const [draggingVisual, setDraggingVisual] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const clearPull = () => {
        startY.current = null;
        distanceRef.current = 0;
        dragging.current = false;
        setDistance(0);
        setDraggingVisual(false);
    };

    const onPointerDown = (event) => {
        if (refreshingRef.current) return;
        if (window.scrollY > 2) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;
        if (event.target.closest("button, a, input, textarea, select, label")) return;

        startY.current = event.clientY;
        dragging.current = true;
        rootRef.current?.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
        if (!dragging.current || startY.current == null || refreshingRef.current) return;
        if (window.scrollY > 2) {
            clearPull();
            return;
        }

        const delta = event.clientY - startY.current;

        if (delta <= 10) {
            if (distanceRef.current !== 0) {
                distanceRef.current = 0;
                setDistance(0);
                setDraggingVisual(false);
            }
            return;
        }

        const eased = Math.min((delta - 10) * 0.55, 72);
        distanceRef.current = eased;
        setDraggingVisual(true);
        setDistance(eased);

        if (event.cancelable) event.preventDefault();
    };

    const onPointerUp = async (event) => {
        if (event?.pointerId != null && rootRef.current?.hasPointerCapture?.(event.pointerId)) {
            rootRef.current.releasePointerCapture(event.pointerId);
        }

        if (!dragging.current) return;

        const shouldRefresh = distanceRef.current >= TRIGGER;
        clearPull();

        if (!shouldRefresh || refreshingRef.current) return;

        refreshingRef.current = true;
        setRefreshing(true);

        try {
            await onRefresh();
        } finally {
            refreshingRef.current = false;
            setRefreshing(false);
        }
    };

    const open = refreshing || distance > 0;
    const ready = distance >= TRIGGER;

    return (
        <div
            ref={rootRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={clearPull}
        >
            <div
                className="overflow-hidden"
                style={{
                    height: refreshing ? 44 : distance,
                    transition: draggingVisual ? "none" : "height 0.22s ease",
                }}
            >
                <div className="h-11 flex items-center justify-center gap-2 text-[#09A5F7]">
                    <span
                        className={`w-4 h-4 rounded-full border-2 border-[#09A5F7]/25 border-t-[#09A5F7] ${
                            refreshing || ready ? "animate-spin" : ""
                        }`}
                    />
                    {open && (
                        <span className="text-xs font-extrabold">
                            {refreshing
                                ? "Refreshing"
                                : ready
                                  ? "Release to refresh"
                                  : "Pull to refresh"}
                        </span>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
}
